import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Mic, Bot, Upload, FolderOpen, Users, CreditCard, Settings, LifeBuoy, Shield, LogOut, Menu, X,
} from "lucide-react";
import { Logo, LangToggle } from "@/components/Brand";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/lib/i18n";
import { UserAvatar } from "./UserAvatar";
import { InstallAppButton } from "./InstallApp";
import { NotificationBell } from "./NotificationBell";

const NAV = [
  ["/app", LayoutDashboard, "Dashboard", "Dashboard", "dashboard", true],
  ["/app/meetings", CalendarDays, "Mesyuarat Saya", "My Meetings", "meetings"],
  ["/app/record", Mic, "Rakam", "Record", "record"],
  ["/app/upload", Upload, "Muat Naik Rakaman", "Upload Recording", "upload"],
  ["/app/bot", Bot, "Mesyuarat Online", "Online Meeting", "bot"],
  ["/app/files", FolderOpen, "Fail & Folder", "Files & Folders", "files"],
  ["/app/workspace", Users, "Workspace & Pasukan", "Workspace & Team", "workspace"],
  ["/app/billing", CreditCard, "Langganan & Penggunaan", "Billing & Usage", "billing"],
  ["/app/settings", Settings, "Tetapan", "Settings", "settings"],
  ["/app/support", LifeBuoy, "Sokongan", "Support", "support"],
];

const WorkspaceSwitcher = () => {
  const { workspace, workspaces, selectWs } = useAuth();
  const { L } = useApp();
  if (!workspace) return null;
  return (
    <Select value={workspace.id} onValueChange={selectWs}>
      <SelectTrigger className="h-10 rounded-xl bg-slate-50 text-left text-sm" data-testid="workspace-switcher" aria-label={L("Tukar workspace", "Switch workspace")}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {workspaces.map((w) => (
          <SelectItem key={w.id} value={w.id} data-testid={`workspace-option-${w.id}`}>
            {w.name} <span className="text-xs text-slate-400">· {w.role}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const NavItems = ({ onClick }) => {
  const { L } = useApp();
  const { user } = useAuth();
  const items = user?.is_super_admin ? [...NAV, ["/app/admin", Shield, "Panel Pentadbir", "Admin Panel", "admin"]] : NAV;
  return items.map(([to, Icon, ms, en, id, end]) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`sidebar-${id}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`
      }
    >
      <Icon size={17} aria-hidden /> {L(ms, en)}
    </NavLink>
  ));
};

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { L } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => {
    await logout();
    navigate("/");
  };
  const sidebar = (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <Logo to="/app" />
        <span className="hidden lg:block"><NotificationBell /></span>
        <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Tutup menu" data-testid="sidebar-close-btn"><X size={20} /></button>
      </div>
      <WorkspaceSwitcher />
      <nav className="slim-scroll -mx-1 flex-1 space-y-0.5 overflow-y-auto px-1" aria-label="Navigasi aplikasi">
        <NavItems onClick={() => setOpen(false)} />
      </nav>
      <InstallAppButton className="mb-3 w-full" testId="sidebar-install-btn" />
      <div className="border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between gap-2 px-1">
          <div className="flex min-w-0 items-center gap-2.5">
            <UserAvatar user={user} testId="sidebar-user-avatar" />
            <div className="min-w-0">
            <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <LangToggle />
        </div>
        <button onClick={doLogout} data-testid="logout-btn" className="mt-2 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">
          <LogOut size={16} /> {L("Log keluar", "Log out")}
        </button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#F8FAFC] lg:flex">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-2">{L("Langkau ke kandungan", "Skip to content")}</a>
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-slate-200 bg-white lg:block">{sidebar}</aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
        <Logo to="/app" />
        <span className="flex items-center gap-2"><NotificationBell testId="mobile-notification-bell" /><button onClick={() => setOpen(true)} aria-label="Buka menu" data-testid="mobile-menu-btn"><Menu size={22} /></button></span>
      </header>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl">{sidebar}</aside>
        </div>
      )}
      <main id="main" className="min-w-0 flex-1">{children}</main>
    </div>
  );
};
